import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset! Please log in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] nerd-gradient flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md animate-fade-up">
        <div className="bg-nerd-card border border-black/8 rounded-3xl p-8 shadow-2xl">
          <div className="w-11 h-11 rounded-2xl bg-nerd-light flex items-center justify-center mb-6">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Set a new password</h1>
          <p className="text-nerd-light/50 text-sm mb-7">Choose a new password for your account.</p>
          {!token ? (
            <div className="text-sm text-nerd-light/60 leading-relaxed">
              This reset link is missing or invalid. <Link to="/forgot-password" data-testid="request-new-link" className="text-nerd-light hover:underline font-medium">Request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">New password</label>
                <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="••••••••" />
              </div>
              <div>
                <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Confirm password</label>
                <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint transition-colors" placeholder="••••••••" />
              </div>
              <button data-testid="reset-submit" disabled={loading} type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </button>
            </form>
          )}
          <p className="text-sm text-nerd-light/50 mt-6 text-center">
            <Link to="/login" data-testid="go-login" className="text-nerd-light hover:underline">Back to log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
